import { useEffect, useState, useCallback } from "react";
import { Copy, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL, fmtDate } from "../lib/format";
import { StatusBadge } from "./StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const fmtGap = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86400)} dia(s)`;
};
const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" }); } catch { return "—"; }
};

export const DuplicateOrdersDialog = ({ open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/orders/duplicates");
      setData(d);
    } catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const removeOrder = async (o) => {
    if (!window.confirm(`Excluir o pedido #${o.code} (${o.client_name}, ${fmtBRL(o.total_value)})? Os lançamentos financeiros vinculados também serão removidos.`)) return;
    try {
      await api.delete(`/orders/${o.id}`);
      toast.success(`Pedido #${o.code} excluído`);
      load();
      onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="duplicates-dialog" className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-3xl max-h-[94dvh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Copy size={20} className="text-amber-400" /> Possíveis Pedidos Duplicados
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Pedidos do mesmo cliente, com o mesmo valor e a mesma data. Revise e exclua manualmente os que forem duplicatas confirmadas — grupos criados com poucos minutos de diferença aparecem primeiro.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-zinc-500 py-4">Analisando pedidos...</p>}
        {!loading && data && data.groups.length === 0 && (
          <p data-testid="duplicates-empty" className="text-sm text-emerald-400 bg-emerald-400/5 border border-emerald-400/20 rounded px-4 py-3">
            Nenhum possível duplicado encontrado. Tudo certo!
          </p>
        )}
        {!loading && data && data.groups.map((g, gi) => (
          <div key={`${g.client_name}-${g.total_value}-${g.order_date}`} data-testid={`duplicate-group-${gi}`}
            className={`border rounded-lg overflow-hidden ${g.close_in_time ? "border-amber-500/40" : "border-zinc-800"}`}>
            <div className={`px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm ${g.close_in_time ? "bg-amber-500/10" : "bg-zinc-800/40"}`}>
              <span className="font-medium text-zinc-100">{g.client_name}</span>
              <span className="text-emerald-400 font-semibold">{fmtBRL(g.total_value)}</span>
              <span className="text-zinc-500">{fmtDate(g.order_date)}</span>
              <span className="text-zinc-400">{g.count} pedidos</span>
              {g.close_in_time && (
                <span className="flex items-center gap-1 text-amber-400 text-xs font-medium">
                  <Warning size={13} weight="fill" /> criados com {fmtGap(g.min_gap_seconds)} de diferença
                </span>
              )}
            </div>
            <div className="divide-y divide-zinc-800/60">
              {g.orders.map((o) => (
                <div key={o.id} data-testid={`duplicate-order-${o.code}`} className="px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                  <span className="text-zinc-300 font-medium">#{o.code}</span>
                  <span className="text-zinc-500 text-xs">criado em {fmtTime(o.created_at)}</span>
                  {o.nf_number && <span className="text-zinc-500 text-xs">NF {o.nf_number}</span>}
                  <StatusBadge status={o.payment_status || "Pendente"} />
                  {(o.paid_total || 0) > 0 && (
                    <span className="text-xs text-amber-400">já recebeu {fmtBRL(o.paid_total)}</span>
                  )}
                  <button data-testid={`duplicate-delete-${o.code}`} onClick={() => removeOrder(o)} title="Excluir este pedido"
                    className="ml-auto flex items-center gap-1.5 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 text-xs font-medium px-2.5 py-1.5 rounded transition-colors">
                    <Trash size={14} /> Excluir
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}

        <button data-testid="duplicates-close-button" onClick={onClose}
          className="w-full border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-sm font-medium py-2.5 rounded transition-colors">
          Fechar
        </button>
      </DialogContent>
    </Dialog>
  );
};
